import React from 'react'
import { ArrowDown, ArrowUp, Medal, Minus } from 'lucide-react'

import { cn } from '@rewardops/ui'

import { Avatar, AvatarFallback } from './ui/avatar'
import { Badge } from './ui/badge'

interface LeaderboardItemProps {
  rank: number
  name: string
  points: number
  rankChange?: number
  isCurrentUser?: boolean
  currentUserLabel?: string
  testIdPrefix?: string
  currencies?: []
}

export default function LeaderboardItem({
  rank,
  name,
  points,
  rankChange,
  isCurrentUser = false,
  currentUserLabel = '(You)',
  testIdPrefix = '',
  currencies,
}: LeaderboardItemProps) {
  const getRankColor = (rank: number) => {
    if (rank === 1) return 'bg-rank-gold text-rank-gold-foreground'
    if (rank === 2) return 'bg-rank-silver text-rank-silver-foreground'
    if (rank === 3) return 'bg-rank-bronze text-rank-bronze-foreground'
    return 'bg-muted text-muted-foreground'
  }

  const getTestId = (suffix: string) => {
    return testIdPrefix ? `${testIdPrefix}-${suffix}` : suffix
  }

  return (
    <div
      className={cn(
        'hover-elevate flex items-center gap-4 rounded-md p-4',
        isCurrentUser && 'bg-tenant-primary-color/5 border border-primary/20',
      )}
      data-testid={getTestId(`leaderboard-item-${rank}`)}
    >
      <div className="flex flex-1 items-center gap-3">
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
            getRankColor(rank),
          )}
          data-testid={getTestId(`text-rank-${rank}`)}
        >
          {rank <= 3 ? <Medal className="h-4 w-4" /> : rank}
        </div>

        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'truncate font-medium',
              isCurrentUser && 'text-primary',
            )}
            data-testid={getTestId(`text-name-${rank}`)}
          >
            {name} {isCurrentUser ? ` ${currentUserLabel}` : ''}
          </p>
          {currencies?.length ? (
            <div className="mt-2 flex gap-4">
              {currencies?.map((currency: any, index: number) => (
                <React.Fragment key={currency?.currencyClassifierName}>
                  <p
                    className="text-sm text-muted-foreground "
                    data-testid={getTestId(
                      `text-points-${rank}-${currency?.currencyClassifierName}`,
                    )}
                  >
                    {currency?.totalAmount?.toLocaleString()}{' '}
                    {currency?.currencyName}
                  </p>
                </React.Fragment>
              ))}
            </div>
          ) : (
            <p
              className="text-sm text-muted-foreground"
              data-testid={getTestId(`text-points-${rank}`)}
            >
              {points.toLocaleString()} points
            </p>
          )}
        </div>
      </div>
      {rankChange !== undefined && (
        <Badge
          variant="outline"
          className="gap-1"
          data-testid={getTestId(`badge-rank-change-${rank}`)}
        >
          {rankChange > 0 ? (
            <ArrowUp className="text-success h-3 w-3" />
          ) : rankChange < 0 ? (
            <ArrowDown className="h-3 w-3 text-destructive" />
          ) : (
            <Minus className="h-3 w-3" />
          )}
          {rankChange !== 0 && (
            <span
              className={cn(
                rankChange > 0 && 'text-success',
                rankChange < 0 && 'text-destructive',
              )}
            >
              {Math.abs(rankChange)}
            </span>
          )}
        </Badge>
      )}
    </div>
  )
}
